import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Home.module.css";
import { auth, db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity } from "../context/EffectiveIdentityContext";
import { tolerantId } from "../utils/firestoreHelpers";
import { formatCurrencyUY } from "../utils/formatters";
import { filterPaymentsVisibleToResidents } from "../utils/cierrePublicacion";
import { leerPagosParaResidente } from "../utils/consultasEdificio";
import { leerPropiasDelEdificio } from "../utils/consultasPropias";
import { leerFichaResidente } from "../utils/fichaResidente";
import { BarChart3, Settings, Building2, Phone } from "lucide-react";
import { FaExclamationTriangle, FaFileInvoiceDollar, FaClock, FaCheckCircle, FaTools, FaNewspaper, FaSwimmingPool, FaFileAlt, FaBell, FaChevronDown, FaCheck } from "react-icons/fa";
import FirstLoginBanner from "../components/FirstLoginBanner";
import AsambleaCard from "../components/asambleas/AsambleaCard";
import { asambleaPropietario } from "../utils/asambleas/api";
import { useUserProfile } from "../hooks/useUserProfile";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppCard,
  BalanceCard,
  Tile,
  SectionLabel,
  AppButton,
  Avatar,
} from "../components/app";
import { appColors, appSpacing, appType, appShadows } from "../utils/appTheme";

/** userid canónico para comparar: 102, "102" y "0102" son el mismo. */
const claveUserid = (v) => {
  const s = String(v ?? "").trim();
  return /^\d+$/.test(s) ? String(Number(s)) : s;
};

/** Un valor con espacios no es un userid: es un nombre escrito a mano. */
const esNombreEscrito = (v) => /\s/.test(String(v ?? "").trim());

// Comisión del edificio: el campo guarda el userid (EditarEdificio) y
// building.commissionNames[campo] (contrato C10), el nombre que se guardó con él.
const ROLES_COMISION = [
  { campo: "commissionPresidente", clave: "presidente" },
  { campo: "commissionSecretario", clave: "secretario" },
  { campo: "commissionTesorero", clave: "tesorero" },
  { campo: "commissionPrimerVocal", clave: "primerVocal" },
  { campo: "commissionSegundoVocal", clave: "segundoVocal" },
];

const Home = ({ setSelected }) => {
  const { t } = useTranslation("home");
  const { isFirstLogin } = useUserProfile();
  const isMobile = useIsMobile();
  const [bldgMenuOpen, setBldgMenuOpen] = useState(false);
  const [name, setName] = useState(t("defaults.user"));
  const [admins, setAdmins] = useState([]); // Lista de administradores del edificio
  const [commissionMembers, setCommissionMembers] = useState([]); // Miembros de la comisión
  const [condition, setCondition] = useState(null);
  const [userUnits, setUserUnits] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [buildingStats, setBuildingStats] = useState({
    units: 0,
    floors: null,
    elevators: null
  });

  // Financial data
  const [saldoPendiente, setSaldoPendiente] = useState(0);
  const [proximaExpensa, setProximaExpensa] = useState(null);
  const [ultimoPago, setUltimoPago] = useState(null);
  const [incidenciasAbiertas, setIncidenciasAbiertas] = useState(0);
  const [loadingFinancial, setLoadingFinancial] = useState(true);
  const [diasMora, setDiasMora] = useState(0);
  const [multasPendientes, setMultasPendientes] = useState(0);

  const { selectedBuilding, selectedUnit, userUnitsInBuilding, buildings, setSelectedBuilding, soloAlquilerEdificio } = useBuilding();
  // viendoComo: "Ver como residente" (quien mira es staff y lee el edificio entero).
  const { email: effEmail, userid: effUserid, readOnly: viendoComo, viewingAs } = useEffectiveIdentity();
  const { t: tAsm } = useTranslation("asambleas");

  // Asambleas: solo propietarios vigentes (igual que el menú y el servidor).
  const isCurrentOwner = (userUnitsInBuilding || []).some(
    (u) => u.userRole === "Propietario" && !u.isHistorical
  );
  const [proximaAsamblea, setProximaAsamblea] = useState(null);
  const cargarAsamblea = React.useCallback(async () => {
    if (!isCurrentOwner || !selectedBuilding?.buildingid) {
      setProximaAsamblea(null);
      return;
    }
    try {
      const res = await asambleaPropietario("listar", { buildingId: selectedBuilding.buildingid });
      const prox = (res.asambleas || [])
        .filter((a) => a.estado === "convocada" && Number(a.inicioMs) > Date.now())
        .sort((a, b) => Number(a.inicioMs) - Number(b.inicioMs))[0];
      setProximaAsamblea(prox || null);
    } catch (_) {
      // Módulo apagado o sin despliegue: el Inicio no muestra la tarjeta.
      setProximaAsamblea(null);
    }
  }, [isCurrentOwner, selectedBuilding]);
  useEffect(() => {
    cargarAsamblea();
  }, [cargarAsamblea]);

  const buildingAddress = selectedBuilding
    ? `${selectedBuilding.address}, ${selectedBuilding.city}, ${selectedBuilding.department || selectedBuilding.country}`
    : "";

  useEffect(() => {
    const fetchDatos = async () => {
      if (!selectedBuilding || !selectedBuilding.buildingid) return;

      try {
        if (!effEmail) return;

        // Ficha del residente (en "ver como", del contexto: quien mira es staff
        // y con C10 no lista users por email).
        const ficha = await leerFichaResidente(db, {
          email: effEmail,
          userid: effUserid,
          viendoComo,
          nombre: viewingAs?.name,
        });
        if (!ficha) return;

        const userData = ficha.data;
        setName(userData.name || t("defaults.user"));
        const userId = ficha.userid;
        setCurrentUserId(userId);
        const normalizedUserId = String(userId);

        const userBuildingQuery = query(
          collection(db, "userbuilding"),
          where("user", "in", tolerantId(userId)),
          where("building", "in", tolerantId(selectedBuilding.buildingid))
        );
        const ubSnap = await getDocs(userBuildingQuery);
        if (!ubSnap.empty) {
          const ubData = ubSnap.docs[0].data();
          setCondition(ubData.condition || null);
        }

        // Fetch user units from apartments
        const userUnitsQuery = query(
          collection(db, "apartments"),
          where("building", "in", tolerantId(selectedBuilding.buildingid))
        );
        const unitsSnap = await getDocs(userUnitsQuery);
        const allUnits = unitsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        setBuildingStats({
          units: allUnits.length,
          floors: selectedBuilding.floors || null,
          elevators: selectedBuilding.elevators || null
        });

        const uidStr = String(userId);
        const uidNum = Number(userId);
        const match = (val) =>
          val != null && (String(val) === uidStr || (!isNaN(uidNum) && Number(val) === uidNum));
        const userUnitsList = allUnits
          .filter(
            (unit) =>
              match(unit.owner) ||
              match(unit.tenant) ||
              match(unit.ownerSecondary) ||
              match(unit.tenantSecondary)
          )
          .map(unit => ({
            ...unit,
            userRole: match(unit.owner) || match(unit.ownerSecondary) ? "Propietario" : "Inquilino",
          }));
        setUserUnits(userUnitsList);

        // Administradores y comisión del edificio sin leer fichas ajenas de
        // `users` (C10: un residente solo lee la propia). El admin sale del doc
        // `admin` (name y photoURL cuando los tiene; si no, "Administrador") y
        // la comisión de building.commissionNames, que EditarEdificio guarda
        // junto con los userids. Un valor con espacios es un nombre escrito a
        // mano; el residente que integra la comisión se ve con su propio nombre.
        let adminDocs = [];
        try {
          const adminSnap = await getDocs(
            query(collection(db, "admin"), where("buildingid", "in", tolerantId(selectedBuilding.buildingid)))
          );
          adminDocs = adminSnap.docs.map((d) => d.data());
        } catch (e) {
          console.error("Error cargando administradores:", e);
        }
        setAdmins(
          adminDocs.map((a) => ({
            userid: a.userid,
            name: (typeof a.name === "string" && a.name.trim()) || t("defaults.admin"),
            photoURL: a.photoURL || null,
          }))
        );
        const nombresComision = selectedBuilding.commissionNames || {};
        const nombrePropio = (userData.name || userData.NombreCompleto || "").trim();
        setCommissionMembers(
          ROLES_COMISION.filter(
            (r) => selectedBuilding[r.campo] != null && String(selectedBuilding[r.campo]).trim() !== ""
          ).map((r) => {
            const valor = String(selectedBuilding[r.campo]).trim();
            const guardado = typeof nombresComision[r.campo] === "string" ? nombresComision[r.campo].trim() : "";
            const esPropio = !esNombreEscrito(valor) && claveUserid(valor) === claveUserid(userId);
            return {
              role: t(`commission.roles.${r.clave}`),
              name:
                guardado ||
                (esNombreEscrito(valor) ? valor : "") ||
                (esPropio ? nombrePropio : "") ||
                t("defaults.noName"),
            };
          })
        );

        // Fetch financial data: payments
        const visiblePaymentUserIds = new Set([normalizedUserId]);
        userUnitsList.forEach((unit) => {
          if (match(unit.ownerSecondary) && unit.owner != null && String(unit.owner).trim() !== "") {
            visiblePaymentUserIds.add(String(unit.owner));
          }
          if (match(unit.tenantSecondary) && unit.tenant != null && String(unit.tenant).trim() !== "") {
            visiblePaymentUserIds.add(String(unit.tenant));
          }
        });
        // El ex-ocupante solo lee sus pagos (reglas 0D): si la consulta por
        // edificio se niega, leerPagosParaResidente la repite fijando sus
        // userid. En "ver como" no hay segundo intento (quien mira es staff).
        const allBuildingPayments = await leerPagosParaResidente(db, selectedBuilding.buildingid, {
          uid: viendoComo ? null : auth.currentUser?.uid || null,
        });
        const ownPayments = allBuildingPayments.filter(
          (p) => String(p.userid) === normalizedUserId
        );
        const sourcePayments =
          ownPayments.length > 0
            ? ownPayments
            : allBuildingPayments.filter((p) => visiblePaymentUserIds.has(String(p.userid)));
        const payments = filterPaymentsVisibleToResidents(sourcePayments);

        // Saldo pendiente
        const pendientes = payments.filter(p => !p.paid);
        const totalPendiente = pendientes.reduce((sum, p) => sum + (Number(p.remainingAmount) || Number(p.amount) || 0), 0);
        setSaldoPendiente(totalPendiente);

        // Dias de mora (del pago vencido mas antiguo)
        const now = new Date();
        let maxDias = 0;
        pendientes.forEach(p => {
          if (!p.maturity) return;
          let mat;
          if (p.maturity.seconds) mat = new Date(p.maturity.seconds * 1000);
          else if (typeof p.maturity === "string" && p.maturity.includes("/")) {
            const parts = p.maturity.split("/");
            mat = new Date(parts[2], parts[1] - 1, parts[0]);
          } else mat = new Date(p.maturity);
          const diff = Math.floor((now - mat) / (1000 * 60 * 60 * 24));
          if (diff > maxDias) maxDias = diff;
        });
        setDiasMora(maxDias);

        // Multas pendientes: las activas de las que es responsable (el
        // responsibleUserId, o si no tiene, el userid), como en Multas. Cada
        // consulta fija el edificio y el titular, así que vale también para el
        // ex-ocupante (C12: leeEdificioOPropio mira userid o responsibleUserId).
        try {
          const activas = await leerPropiasDelEdificio(db, "multas", selectedBuilding.buildingid, {
            soloPropias: true,
            campos: ["userid", "responsibleUserId"],
            propios: [userId],
            extras: [where("status", "==", "activa")],
          });
          const miClave = claveUserid(userId);
          setMultasPendientes(
            activas.filter((m) => claveUserid(m.responsibleUserId || m.userid || m.userId) === miClave).length
          );
        } catch (e) { /* multas collection may not exist yet */ }

        // Proxima expensa (pendiente con vencimiento mas cercano)
        const conVencimiento = pendientes.filter(p => p.maturity);
        if (conVencimiento.length > 0) {
          conVencimiento.sort((a, b) => {
            const parseDate = (d) => {
              if (!d) return new Date(9999, 0);
              if (d.seconds) return new Date(d.seconds * 1000);
              if (typeof d === "string" && d.includes("/")) {
                const parts = d.split("/");
                return new Date(parts[2], parts[1] - 1, parts[0]);
              }
              return new Date(d);
            };
            return parseDate(a.maturity) - parseDate(b.maturity);
          });
          setProximaExpensa(conVencimiento[0]);
        }

        // Ultimo pago realizado
        const pagados = payments.filter(p => p.paid && p.paymentDate);
        if (pagados.length > 0) {
          pagados.sort((a, b) => {
            const toTs = (d) => {
              if (!d) return 0;
              if (d.seconds) return d.seconds;
              return new Date(d).getTime() / 1000;
            };
            return toTs(b.paymentDate) - toTs(a.paymentDate);
          });
          setUltimoPago(pagados[0]);
        }

        // Incidencias abiertas del usuario (tolerante a tipo string/number)
        const incQ = query(
          collection(db, "incidents"),
          where("buildingid", "in", tolerantId(selectedBuilding.buildingid)),
          where("userid", "in", tolerantId(normalizedUserId))
        );
        const incSnap = await getDocs(incQ);
        const incidentDocs = incSnap.docs;
        const incidents = incidentDocs.map(d => d.data());
        const abiertas = incidents.filter(i => i.status !== "Resuelta" && i.status !== "Cerrada" && i.status !== "resuelta" && i.status !== "cerrada");
        setIncidenciasAbiertas(abiertas.length);

      } catch (error) {
        console.error("Error al traer datos del usuario o edificio:", error);
      } finally {
        setLoadingFinancial(false);
      }
    };

    fetchDatos();
  }, [selectedBuilding, effEmail, effUserid, viendoComo, viewingAs?.name]);

  const displayUnit = selectedUnit || userUnitsInBuilding?.[0] || userUnits[0];
  const getUserRoleText = () => {
    if (!displayUnit) return condition || "";
    const u = displayUnit;
    const role = u.userRole || (String(u.owner) === String(currentUserId) ? "Propietario" : "Inquilino");
    // El valor crudo ("Propietario"/"Inquilino") se usa como identificador de
    // lógica en otras pantallas; acá solo se traduce para mostrar.
    return role === "Propietario" ? t("roles.owner") : role === "Inquilino" ? t("roles.tenant") : role;
  };

  // ─── Versión mobile: estilo app "Radial" ────────────────────────────────────
  if (isMobile) {
    const firstName = (name || "").split(" ")[0] || t("defaults.user");
    const initials = (name || "U")
      .split(" ")
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("");
    const balanceRows = [];
    if (proximaExpensa) {
      balanceRows.push({
        k: soloAlquilerEdificio ? t("financial.nextRent") : t("financial.nextExpense"),
        sub: proximaExpensa.maturity ? t("due", { date: proximaExpensa.maturity }) : undefined,
        v: formatCurrencyUY(proximaExpensa.amount),
      });
    }
    if (ultimoPago) {
      balanceRows.push({ k: t("financial.lastPayment"), v: formatCurrencyUY(ultimoPago.amount) });
    }

    const stats = [
      buildingStats.units > 0 ? { n: buildingStats.units, l: t("stats.units") } : null,
      buildingStats.floors != null ? { n: buildingStats.floors, l: t("stats.floors") } : null,
      buildingStats.elevators != null ? { n: buildingStats.elevators, l: t("stats.elevators") } : null,
    ].filter(Boolean);

    return (
      <AppScreen
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 0 14px",
              paddingTop: "calc(6px + env(safe-area-inset-top, 0px))",
            }}
          >
            <button
              type="button"
              aria-label={t("aria.profile")}
              onClick={() => setSelected && setSelected("perfil")}
              style={{ border: "none", background: "transparent", padding: 0, margin: 0, cursor: "pointer", borderRadius: 12, flexShrink: 0, lineHeight: 0 }}
            >
              <Avatar initials={initials} shape="square" size={44} />
            </button>
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
              <span style={{ ...appType.greeting }}>{t("greeting", { name: firstName })}</span>
              {buildings && buildings.length > 1 ? (
                <div style={{ position: "relative", alignSelf: "flex-start", maxWidth: "100%" }}>
                  <button
                    type="button"
                    aria-label={t("aria.changeBuilding")}
                    aria-expanded={bldgMenuOpen}
                    onClick={() => setBldgMenuOpen((o) => !o)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      maxWidth: "100%",
                    }}
                  >
                    <span style={{ ...appType.bodySm, fontSize: 13, color: appColors.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {selectedBuilding?.name || t("chooseBuilding")}
                    </span>
                    <FaChevronDown style={{ fontSize: 10, color: appColors.ink3, flexShrink: 0, transform: bldgMenuOpen ? "rotate(180deg)" : "none", transition: "transform .15s ease" }} />
                  </button>
                  {bldgMenuOpen && (
                    <>
                      <div
                        onClick={() => setBldgMenuOpen(false)}
                        style={{ position: "fixed", inset: 0, zIndex: 40 }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 8px)",
                          left: 0,
                          zIndex: 50,
                          minWidth: 220,
                          maxWidth: 300,
                          background: appColors.surface,
                          borderRadius: 16,
                          boxShadow: appShadows.hero,
                          border: `1px solid ${appColors.line2}`,
                          padding: 6,
                          maxHeight: 300,
                          overflowY: "auto",
                        }}
                      >
                        {buildings.map((b) => {
                          const active = String(b.buildingid) === String(selectedBuilding?.buildingid);
                          return (
                            <button
                              key={b.buildingid}
                              type="button"
                              onClick={() => {
                                if (setSelectedBuilding) setSelectedBuilding(b);
                                setBldgMenuOpen(false);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                width: "100%",
                                textAlign: "left",
                                border: "none",
                                background: active ? appColors.surface2 : "transparent",
                                borderRadius: 10,
                                padding: "11px 12px",
                                cursor: "pointer",
                              }}
                            >
                              <span style={{ ...appType.body, fontSize: 14, flex: 1, minWidth: 0 }}>{b.name}</span>
                              {active && <FaCheck style={{ color: appColors.blue, fontSize: 12, flexShrink: 0 }} />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                selectedBuilding?.name && (
                  <span style={{ ...appType.bodySm, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {selectedBuilding.name}
                    {displayUnit?.number != null && ` · ${t("unit", { number: displayUnit.number })}`}
                  </span>
                )
              )}
            </div>
            <button
              type="button"
              aria-label={t("aria.news")}
              onClick={() => setSelected && setSelected("novedades")}
              style={{
                width: 42,
                height: 42,
                minWidth: 42,
                borderRadius: "50%",
                border: `1px solid ${appColors.line}`,
                background: appColors.surface,
                color: appColors.ink,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                position: "relative",
              }}
            >
              <FaBell style={{ fontSize: 15 }} />
              {incidenciasAbiertas > 0 && (
                <span style={{ position: "absolute", top: 9, right: 10, width: 7, height: 7, borderRadius: "50%", background: appColors.red }} />
              )}
            </button>
          </div>
        }
      >
        {/* Brand row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "2px 0 20px" }}>
          <svg width="26" height="22" viewBox="0 0 240 200" style={{ display: "block", flexShrink: 0 }}>
            <rect x="20" y="0" width="40" height="200" fill={appColors.blue} />
            <rect x="100" y="65" width="40" height="70" fill={appColors.blue} />
            <rect x="180" y="0" width="40" height="200" fill={appColors.blue} />
          </svg>
          <span style={{ width: 1, height: 24, background: appColors.line }} />
          <span style={{ ...appType.caption, fontSize: 11.5, lineHeight: 1.3 }}>
            {t("tagline.line1")}<br />{t("tagline.line2")}
          </span>
        </div>

        {isFirstLogin && (
          <div style={{ marginBottom: appSpacing.md }}>
            <FirstLoginBanner displayName={name} />
          </div>
        )}

        <BalanceCard
          label={t("balance.label")}
          amount={loadingFinancial ? 0 : saldoPendiente}
          amountTone={saldoPendiente > 0 ? "red" : "green"}
          moraLabel={!loadingFinancial && diasMora > 0 ? t("moraDays", { count: diasMora }) : undefined}
          rows={balanceRows}
          primaryAction={{ label: t("balance.pay"), onClick: () => setSelected && setSelected("expensas") }}
          secondaryAction={{ label: t("balance.statement"), onClick: () => setSelected && setSelected("cuentas") }}
          style={{ marginBottom: appSpacing.section }}
        />

        {!loadingFinancial && multasPendientes > 0 && (
          <AppCard
            accent="blue"
            onClick={() => setSelected && setSelected("multasresidente")}
            style={{ marginBottom: appSpacing.section, borderLeftColor: appColors.red }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <FaExclamationTriangle style={{ color: appColors.red, flexShrink: 0 }} />
              <span style={{ ...appType.body }}>
                {t("multasPending", { count: multasPendientes })}
              </span>
            </div>
          </AppCard>
        )}

        {proximaAsamblea && (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>{tAsm("home.proxima")}</SectionLabel>
            <div style={{ marginBottom: appSpacing.section }}>
              <AsambleaCard a={proximaAsamblea} onCambio={cargarAsamblea} />
            </div>
          </>
        )}

        <SectionLabel style={{ marginBottom: 12 }}>{t("sections.accesos")}</SectionLabel>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: appSpacing.gridGap,
            marginBottom: appSpacing.section,
          }}
        >
          <Tile
            layout="row"
            icon={<FaTools />}
            name={t("tiles.incidencias.name")}
            subtitle={incidenciasAbiertas > 0 ? t("openCount", { count: incidenciasAbiertas }) : t("tiles.incidencias.reportar")}
            badge={incidenciasAbiertas}
            onClick={() => setSelected && setSelected("incidencias_mis")}
          />
          <Tile
            layout="row"
            icon={<FaNewspaper />}
            name={t("tiles.novedades.name")}
            subtitle={t("tiles.novedades.subtitle")}
            onClick={() => setSelected && setSelected("novedades")}
          />
          <Tile
            layout="row"
            icon={<FaSwimmingPool />}
            name={t("tiles.amenities.name")}
            subtitle={t("tiles.amenities.subtitle")}
            onClick={() => setSelected && setSelected("amenities")}
          />
          <Tile
            layout="row"
            icon={<FaFileAlt />}
            name={t("tiles.documentos.name")}
            subtitle={t("tiles.documentos.subtitle")}
            onClick={() => setSelected && setSelected("documentos")}
          />
        </div>

        {stats.length > 0 && (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>{t("sections.edificio")}</SectionLabel>
            <AppCard style={{ marginBottom: appSpacing.section }}>
              <div style={{ display: "flex" }}>
                {stats.map((s, i) => (
                  <div
                    key={s.l}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      borderLeft: i > 0 ? `1px solid ${appColors.line2}` : "none",
                    }}
                  >
                    <div style={{ ...appType.stat, fontSize: 22 }}>{s.n}</div>
                    <div style={{ ...appType.statLabel, marginTop: 4 }}>{s.l}</div>
                  </div>
                ))}
              </div>
              {buildingAddress && (
                <p style={{ ...appType.bodySm, margin: "14px 0 0", textAlign: "center" }}>
                  {buildingAddress}
                </p>
              )}
            </AppCard>
          </>
        )}

        {admins.length > 0 && (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>{t("sections.administracion")}</SectionLabel>
            <AppCard style={{ marginBottom: appSpacing.section }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {admins.map((admin, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Avatar
                      initials={(admin.name || "A").charAt(0).toUpperCase()}
                      photoURL={admin.photoURL}
                      shape="circle"
                      size={40}
                    />
                    <span style={{ ...appType.body, fontSize: 14 }}>{admin.name}</span>
                  </div>
                ))}
              </div>
              <AppButton
                variant="outline"
                block
                icon={<Phone size={15} />}
                style={{ marginTop: 16 }}
              >
                {t("contact")}
              </AppButton>
            </AppCard>
          </>
        )}

        {commissionMembers.length > 0 && (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>{t("sections.comision")}</SectionLabel>
            <AppCard>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {commissionMembers.map((member, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span style={{ ...appType.statLabel }}>{member.role}</span>
                    <span style={{ ...appType.body, fontSize: 14 }}>{member.name}</span>
                  </div>
                ))}
              </div>
              {isCurrentOwner && (
                <AppButton
                  variant="primary"
                  block
                  onClick={() => setSelected && setSelected("reuniones")}
                  style={{ marginTop: 16 }}
                >
                  {t("viewMinutes")}
                </AppButton>
              )}
            </AppCard>
          </>
        )}
      </AppScreen>
    );
  }

  // ─── Versión desktop (existente) ─────────────────────────────────────────────
  return (
    <div className={styles.page}>
      {isFirstLogin && <FirstLoginBanner displayName={name} />}

      {/* Welcome Card */}
      <div className={styles.welcomeCard}>
        <div className={styles.welcomeLeft}>
          <h1 className={styles.welcomeTitle}>{t("welcome.title", { name })}</h1>
          <p className={styles.welcomeSubtitle}>
            {t("welcome.subtitle", { building: selectedBuilding?.name || t("welcome.buildingFallback") })}
            {displayUnit && ` · ${t("unitColon", { number: displayUnit.number ?? "—" })}`}
          </p>
        </div>

        <div className={styles.welcomeRight}>
          <div className={styles.locationInfo}>
            <div className={styles.locationIcon}>
              <Building2 size={20} />
            </div>
            <div className={styles.locationDetails}>
              <div className={styles.locationAddress}>{buildingAddress}</div>
              {selectedBuilding && (
                <div className={styles.locationUnit}>
                  {t("unit", { number: displayUnit?.number ?? "—" })}
                  {getUserRoleText() && ` · ${getUserRoleText()}`}
                </div>
              )}
            </div>
          </div>

          <div className={styles.actionButtons}>
            <button className={styles.dashboardBtn}>
              <BarChart3 size={18} />
              <span>{t("dashboard")}</span>
            </button>
            <button className={styles.configBtn} onClick={() => setSelected && setSelected("configuracion")}>
              <Settings size={18} />
              <span>{t("settings")}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className={styles.financialRow}>
        <div
          className={`${styles.financialCard} ${saldoPendiente > 0 ? styles.financialCardWarning : styles.financialCardSuccess}`}
          onClick={() => setSelected && setSelected("expensas")}
          style={{ cursor: "pointer" }}
        >
          <div className={styles.financialIcon}>
            {saldoPendiente > 0 ? <FaClock /> : <FaCheckCircle />}
          </div>
          <div className={styles.financialInfo}>
            <span className={styles.financialLabel}>
              {saldoPendiente > 0 ? t("financial.debtPending") : t("financial.upToDate")}
            </span>
            <span className={styles.financialAmount}>
              {loadingFinancial ? "..." : formatCurrencyUY(saldoPendiente)}
            </span>
            {!loadingFinancial && diasMora > 0 && (
              <span className={styles.financialDetail} style={{ color: "#dc2626", fontWeight: 400 }}>
                {t("moraDays", { count: diasMora })}
              </span>
            )}
          </div>
        </div>

        <div
          className={`${styles.financialCard} ${styles.financialCardNeutral}`}
          onClick={() => setSelected && setSelected("expensas")}
          style={{ cursor: "pointer" }}
        >
          <div className={styles.financialIcon}>
            <FaFileInvoiceDollar />
          </div>
          <div className={styles.financialInfo}>
            <span className={styles.financialLabel}>{soloAlquilerEdificio ? t("financial.nextRent") : t("financial.nextCommonCharge")}</span>
            <span className={styles.financialAmount}>
              {loadingFinancial ? "..." : proximaExpensa ? formatCurrencyUY(proximaExpensa.amount) : "—"}
            </span>
            {proximaExpensa?.maturity && (
              <span className={styles.financialDetail}>{t("dueColon", { date: proximaExpensa.maturity })}</span>
            )}
          </div>
        </div>

        <div className={`${styles.financialCard} ${styles.financialCardNeutral}`}>
          <div className={styles.financialIcon}>
            <FaCheckCircle />
          </div>
          <div className={styles.financialInfo}>
            <span className={styles.financialLabel}>{t("financial.lastPaymentDesktop")}</span>
            <span className={styles.financialAmount}>
              {loadingFinancial ? "..." : ultimoPago ? formatCurrencyUY(ultimoPago.amount) : "—"}
            </span>
            {ultimoPago?.description && (
              <span className={styles.financialDetail}>{ultimoPago.description}</span>
            )}
          </div>
        </div>

        <div
          className={`${styles.financialCard} ${incidenciasAbiertas > 0 ? styles.financialCardWarning : styles.financialCardSuccess}`}
          onClick={() => setSelected && setSelected("incidencias_mis")}
          style={{ cursor: "pointer" }}
        >
          <div className={styles.financialIcon}>
            <FaExclamationTriangle />
          </div>
          <div className={styles.financialInfo}>
            <span className={styles.financialLabel}>{t("financial.openIssues")}</span>
            <span className={styles.financialAmount}>
              {loadingFinancial ? "..." : incidenciasAbiertas}
            </span>
          </div>
        </div>
      </div>

      {/* Multas Alert */}
      {!loadingFinancial && multasPendientes > 0 && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12,
          padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
          marginBottom: 16, cursor: "pointer",
        }} onClick={() => setSelected && setSelected("multasresidente")}>
          <FaExclamationTriangle style={{ color: "#dc2626", flexShrink: 0 }} />
          <span style={{ fontSize: 14, color: "#991b1b", fontWeight: 400 }}>
            {t("multasPending", { count: multasPendientes })}
          </span>
        </div>
      )}

      {proximaAsamblea && (
        <div style={{ marginBottom: 16, maxWidth: 720 }}>
          <h2 className={styles.cardTitle} style={{ marginBottom: 12 }}>{tAsm("home.proxima")}</h2>
          <AsambleaCard a={proximaAsamblea} onCambio={cargarAsamblea} />
        </div>
      )}

      {/* Info Cards Row */}
      <div className={styles.cardsRow}>
        {/* Edificio Card */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>{t("sections.edificio")}</h2>
          <div className={styles.statsGrid}>
            {buildingStats.units > 0 && (
              <div className={styles.statBox}>
                <span className={styles.statNumber}>{buildingStats.units}</span>
                <span className={styles.statLabel}>{t("stats.units").toUpperCase()}</span>
              </div>
            )}
            {buildingStats.floors != null && (
              <div className={styles.statBox}>
                <span className={styles.statNumber}>{buildingStats.floors}</span>
                <span className={styles.statLabel}>{t("stats.floors").toUpperCase()}</span>
              </div>
            )}
            {buildingStats.elevators != null && (
              <div className={styles.statBox}>
                <span className={styles.statNumber}>{buildingStats.elevators}</span>
                <span className={styles.statLabel}>{t("stats.elevators").toUpperCase()}</span>
              </div>
            )}
          </div>
          <p className={styles.cardAddress}>{buildingAddress}</p>
        </div>

        {/* Administración Card */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>{t("sections.administracion")}</h2>
          {admins.length > 0 ? (
            admins.map((admin, i) => (
              <div key={i} className={styles.adminInfo} style={i > 0 ? { marginTop: 12 } : undefined}>
                {admin.photoURL ? (
                  <img src={admin.photoURL} alt={admin.name} className={styles.adminAvatar} loading="lazy" />
                ) : (
                  <div className={styles.adminAvatarLetter}>
                    {admin.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className={styles.adminDetails}>
                  <h3 className={styles.adminName}>{admin.name}</h3>
                </div>
              </div>
            ))
          ) : (
            <p style={{ fontSize: 13, color: "#9ca3af" }}>{t("emptyAdmin")}</p>
          )}
          <button className={styles.contactBtn}>
            <Phone size={16} />
            <span>{t("contact")}</span>
          </button>
        </div>

        {/* Comisión Card */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>{t("sections.comision")}</h2>
          <div className={styles.governmentList}>
            {commissionMembers.length > 0 ? (
              commissionMembers.map((member, i) => (
                <div key={i} className={styles.governmentItem}>
                  <span className={styles.governmentRole}>{member.role.toUpperCase()}</span>
                  <span className={styles.governmentName}>{member.name}</span>
                </div>
              ))
            ) : (
              <p style={{ fontSize: 13, color: "#9ca3af" }}>{t("emptyCommission")}</p>
            )}
          </div>
          {isCurrentOwner && (
            <button className={styles.actasBtn} onClick={() => setSelected && setSelected("reuniones")}>
              {t("viewMinutes")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
